import { useEffect, useState } from "react";
import { createFileRoute, useRouterState } from "@tanstack/react-router";

import "@/compat/historyState";
import { clearEntryIntentFromAddress } from "@/engine/state/entryIntent";
import { EntryScreen } from "@/components/EntryScreen";

/**
 * The way in.
 *
 * It was the root address with a room sometimes drawn on top of it; since #610
 * the room has an address of its own and this one is only ever the door. What a
 * room sent this visitor back with — a refusal, and the code they were last in
 * — arrives in the history entry rather than in the address: it is worth saying
 * once, and not worth a reload finding it again.
 *
 * **It is spent on arrival**, as the room's own address spends what the press
 * sent it (#646). The browser keeps `history.state` across a reload and the
 * server never sees it, so a reload that still found it hydrated the way in on
 * a code and a refusal the served HTML was drawn without (#862).
 */
export const Route = createFileRoute("/home")({
  /**
   * `?room=<code>` is a code in somebody's hand, and opens the Join door on it.
   *
   * It is not the room's address and does not walk anybody in — that is
   * `/room/<code>`, and `/` answers the old query form with it. This is the
   * other thing a code can mean: show me the door, with it already typed.
   */
  validateSearch: (
    search: Record<string, unknown>,
  ): { room?: string; enter?: "create" } => ({
    room: typeof search.room === "string" ? search.room : undefined,
    // A sign-in begun on the way to creating a room comes back with this, and
    // home opens on Create for it rather than on Join (#738).
    ...(search.enter === "create" ? { enter: "create" as const } : {}),
  }),
  component: HomeRoute,
});

function HomeRoute() {
  const navigate = Route.useNavigate();
  const state = useRouterState({ select: (s) => s.location.state });
  // Read on the first render, before the rubbing out below can take it away.
  const [sentBack] = useState(() => ({
    refusal: state.refusal,
    code: state.code,
  }));

  useEffect(() => {
    // A sign-in round trip comes home to `/` and is sent on with its query;
    // what this application put in that query is spent by the time it lands.
    clearEntryIntentFromAddress({ keepRoom: true });
    if (!sentBack.refusal && !sentBack.code) return;
    void navigate({
      to: ".",
      search: true,
      replace: true,
      state: (prev) => ({ ...prev, refusal: undefined, code: undefined }),
    });
  }, [navigate, sentBack.refusal, sentBack.code]);

  const { room, enter } = Route.useSearch();
  // Read once: the address is cleared of it on mount, and the door it opened
  // must not close again when it is.
  const [opensOn] = useState(enter);

  return (
    <EntryScreen
      refusal={sentBack.refusal ?? null}
      prefillCode={room ?? sentBack.code ?? ""}
      opensOn={opensOn}
    />
  );
}
